//library
import React, { useCallback, useState, useMemo, useEffect, useRef } from "react";

//dto
import PartnersTableProps, { PartnersDataDto, PartnersUpdateDto } from "dto/components/pages/partners/page-partner-table-dto";

//components
import AppTextarea from "components/app-textarea";
import AppPagination from "components/app-pagination";

//common
import formatIndexNumber from "common/format-index-number";

//assets
// import arrowUp from "assets/images/components/app-faq-table/arrow-up.svg";
import chevronUp from "assets/images/components/app-faq-table/chevron-up.svg";
// import arrowDown from "assets/images/components/app-faq-table/arrow-down.svg";
import editIcon from "assets/images/components/app-articles-table/edit-icon.svg";
// import wasteBinIcon from "assets/images/components/app-link-input/trash-icon.svg";
import chevronDown from "assets/images/components/app-faq-table/chevron-down.svg";
import AppSelectInput from "components/app-select-input";
import { statusOptions } from "constants/status-options";

const PartnersTable: React.FC<PartnersTableProps> = ({ data, setDisableAddNew, newRow, setNewRow, onUpdatePartners, onCreatePartner, onHandlePageChange, fetching, paginationTable }) => {
	const newRowRef = useRef<HTMLTableRowElement | null>(null);
	const [expandedRows, setExpandedRows] = useState<string[]>([]);
	const [editingRow, setEditingRow] = useState<string | null>();
	const [isReadyData, setIsReadyData] = useState<boolean>(fetching);
	const [partners, setPartners] = useState<PartnersDataDto>(data);
	const [errors, setErrors] = useState<Record<string, Record<string, string>>>({});
	const startIndex = paginationTable.currentPage * paginationTable.tableSize;
	const [partnerUpdatedData, setPartnerUpdatedData] = useState<PartnersUpdateDto>();
	const isEmptyData = useMemo(() => partners.length === 0 && !fetching, [partners.length, fetching]);

	const toggleRow = useCallback((id: string) => {
		setExpandedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
	}, []);

	//prettier-ignore
	const onHandleEdit = useCallback((id: string) => {
        setEditingRow(id);
        setDisableAddNew(true);
    }, [setDisableAddNew]);

	//prettier-ignore
	const onHandleInputChange = useCallback((id: string, field: "name" | "logo" | "status" , value: string) => {
		const updatedPartner = partnerUpdatedData ?? partners.find((partner) => partner.id === id);

		if (updatedPartner) {
			const updatedData = { ...updatedPartner, [field]: value};

			setPartnerUpdatedData(updatedData);

			setErrors((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value.trim() ? "" : "This field is required" }}));
		}
    }, [partners, partnerUpdatedData]);

	//prettier-ignore
	const onHandleNewInputChange = useCallback((field: string, value: string) => {
		setNewRow((prev: any) => ({ ...prev, [field]: value }));

		setErrors((prev) => ({ ...prev, new: { ...prev["new"], [field]: value.trim() ? "" : "This field is required" }}));
	}, [setNewRow]);

	const validateRow = (rowData: any) => {
		const requiredFields = ["name", "logo", "status"];
		const newErrors: Record<string, string> = {};

		requiredFields.forEach((field) => {
			if (!rowData[field]?.trim()) newErrors[field] = "This field is required";
		});
		return newErrors;
	};

	//prettier-ignore
	const onHandleSave = useCallback((id: string) => {
		if (partnerUpdatedData) {
			const rowErrors = validateRow(partnerUpdatedData);

			if (Object.keys(rowErrors).length > 0) {
				setErrors((prev) => ({ ...prev, [id]: rowErrors }));
				return; 
			}

			const newPartner = partners.map((partner) => (partner.id === id ? { ...partner, ...partnerUpdatedData } : partner));

			setPartners(newPartner);
			onUpdatePartners(partnerUpdatedData);
			setIsReadyData(false);
			setEditingRow(null);
			setPartnerUpdatedData(null);
			setDisableAddNew(false);
			setErrors((prev) => ({ ...prev, [id]: {} })); 
		}
	}, [partners, partnerUpdatedData, onUpdatePartners, setDisableAddNew]);

	//prettier-ignore
	// const onHandleMove = useCallback((id: string, move: number) => {
	// 	const index = partners.findIndex((partner) => partner.id === id);
	// 	const updatedPartner = partners[index];

	// 	if ((index > 0  && move === -1) || (index < partners.length - 1 && move === 1)) {
	// 		const newPartners = [...partners];
	// 		const [movedCareer] = newPartners.splice(index, 1);
	// 		const movedDataSortNumber = partners[index + move].sortNumber;
	// 		const updatedData = { ...updatedPartner, sortNumber: movedDataSortNumber };

	// 		if (updatedData) setPartnerUpdatedData(updatedData);

	// 		newPartners.splice(index + move, 0, movedCareer);

	// 	 setPartners(newPartners);
	// 	}
	// }, [partners]);

	//prettier-ignore
	// const onHandleDelete = useCallback((id: string) => {
	// 	const updatedPartner = partners.find((partner) => partner.id === id);

	// 	if (updatedPartner) {
	//         const updatedData = { ...updatedPartner, status: "inactive" };

	// 		const changePage = paginationTable.numberOfElements < 2 && paginationTable.currentPage > 0 ? paginationTable.currentPage - 1 : undefined;

	// 		onUpdatePartners(updatedData, changePage);
	// 	}

	// 	setEditingRow(null);
	// 	setPartnerUpdatedData(null);
	// 	setDisableAddNew(false);
	// }, [partners, paginationTable, onUpdatePartners, setDisableAddNew]);

	const truncateText = useCallback((text: string, maxLength: number) => {
		return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
	}, []);

	const onHandleCancel = () => {
		setNewRow(null);
		setEditingRow(null);
		setDisableAddNew(false);
		setErrors({});
	};

	const onHandleSaveNew = () => {
		if (!newRow) return;
		const rowErrors = validateRow(newRow);

		if (Object.keys(rowErrors).length > 0) {
			setErrors((prev) => ({ ...prev, new: rowErrors }));
			return;
		}

		const newPartner = {
			name: newRow?.name || "",
			logo: newRow?.logo || "",
			status: "active",
		};

		onCreatePartner(newPartner);
		setNewRow(null);
		setDisableAddNew(false);
		setErrors((prev) => ({ ...prev, new: {} }));
	};

	const getLabel = (options: { label: string; value: string }[], value: string) => {
		return options.find((option) => option.value === value)?.label || value;
	};

	useEffect(() => {
		if (!fetching) setIsReadyData(true);
	}, [fetching]);

	useEffect(() => {
		if (!editingRow && isReadyData) setPartners(data);
	}, [partners, editingRow, isReadyData, data]);

	useEffect(() => {
		if (newRow && newRowRef.current) newRowRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
	}, [newRow]);

	return (
		<div className="app-partners-table">
			<div className="partners-table">
				<div className="partners-table__wrapper">
					<table className="partners-table__container">
						<thead className="partners-table__header">
							<tr>
								<th className="partners-table__th">No.</th>
								<th className="partners-table__th">Name</th>
								<th className="partners-table__th">Logo</th>
								<th className="partners-table__th">Status</th>
							</tr>
						</thead>

						<tbody className="partners-table__content">
							{partners.map((job, index) => {
								const isEditing = editingRow === job.id;
								const isExpanded = expandedRows.includes(job.id);
								const rowErrors = errors[job.id] || {};

								return (
									<tr className="partners-table__tr" key={job.id}>
										<td className="partners-table__td">{formatIndexNumber(index + 1 + startIndex)}</td>

										<td className="partners-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppTextarea fieldRequired name="name" error={rowErrors.name} touched={!!partnerUpdatedData} value={partnerUpdatedData?.name ?? job.name} maxCharLength={90} maxRows={20} onChange={(e) => onHandleInputChange(job.id, "name", e.target.value)} />
											) : isExpanded ? (
												job.name
											) : (
												truncateText(job.name, 25)
											)}
										</td>

										<td className="partners-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppTextarea fieldRequired name="logo" error={rowErrors.logo} touched={!!partnerUpdatedData} value={partnerUpdatedData?.logo ?? job.logo} maxRows={100} onChange={(e) => onHandleInputChange(job.id, "logo", e.target.value)} />
											) : isExpanded ? (
												job.logo
											) : (
												truncateText(job.logo, 55)
											)}
										</td>

										<td className="partners-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppSelectInput fieldRequired name="status" error={rowErrors.status} touched={!!partnerUpdatedData} placeholder={job.status} value={partnerUpdatedData?.status ?? job.status} onChange={(e) => onHandleInputChange(job.id, "status", e.target.value)} options={statusOptions} searchable={false} />
											) : (
												<span className={job.status === "active" ? "partners-table__td--active" : "partners-table__td--inactive"}>{getLabel(statusOptions, job.status)}</span>
											)}
										</td>

										<td className="partners-table__td">
											{isEditing ? (
												<div className="partners-table__edit">
													<button className="partners-table__cancel" onClick={onHandleCancel}>
														Cancel
													</button>

													{/* <button className="partners-table__icon-button--bordered" onClick={() => onHandleDelete(job.id)}>
														<img src={wasteBinIcon} alt="delete" />
													</button> */}

													{/* <button className="partners-table__icon-button--bordered" onClick={() => onHandleMove(job.id, -1)}>
														<img src={arrowUp} alt="up" />
													</button>

													<button className="partners-table__icon-button--bordered" onClick={() => onHandleMove(job.id, 1)}>
														<img src={arrowDown} alt="down" />
													</button> */}

													<button className="partners-table__icon-button--save" onClick={() => onHandleSave(job.id)}>
														Save
													</button>
												</div>
											) : (
												<div className="partners-table__actions">
													<button className="partners-table__icon-button" onClick={() => toggleRow(job.id)}>
														<img src={isExpanded ? chevronUp : chevronDown} alt="" />
													</button>

													<button className="partners-table__icon-button" onClick={() => onHandleEdit(job.id)}>
														<img src={editIcon} alt="Edit" />
													</button>
												</div>
											)}
										</td>
									</tr>
								);
							})}

							{newRow && (
								<tr className="partners-table__tr" ref={newRowRef}>
									<td className="partners-table__td">{formatIndexNumber(paginationTable.totalElements + 1)}</td>

									<td className="partners-table__td">
										{/* prettier-ignore */}
										<AppTextarea name="new-name" error={errors["new"]?.name} touched={!!newRow} fieldRequired value={newRow?.name || ""} maxCharLength={90} maxRows={20} placeholder="Enter text here" onChange={(e) => onHandleNewInputChange("name", e.target.value)} />
									</td>

									<td className="partners-table__td">
										{/* prettier-ignore */}
										<AppTextarea name="new-logo" error={errors["new"]?.logo} touched={!!newRow} fieldRequired value={newRow?.logo || ""} maxRows={100} placeholder="Enter text here" onChange={(e) => onHandleNewInputChange("logo", e.target.value)} />
									</td>

									<td className="partners-table__td">
										{/* prettier-ignore */}
										<AppSelectInput fieldRequired name="status" error={errors["new"]?.status} touched={!!newRow} placeholder="Please Select" value={newRow?.status || ""} onChange={(e) => onHandleNewInputChange("status", e.target.value)} options={statusOptions} searchable={false} />
									</td>

									<td className="partners-table__td">
										<div className="partners-table__actions">
											<button className="partners-table__icon-button--cancel" onClick={onHandleCancel}>
												Cancel
											</button>

											<button className="partners-table__icon-button--add" onClick={onHandleSaveNew}>
												Add
											</button>
										</div>
									</td>
								</tr>
							)}

							{fetching && (
								<tr className="partners-table__tr">
									<td className="partners-table__message" colSpan={6}>
										Loading...
									</td>
								</tr>
							)}
							{isEmptyData && (
								<tr className="partners-table__tr">
									<td className="partners-table__message" colSpan={6}>
										No records found
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				{!isEmptyData && !fetching && (
					<AppPagination currentPage={paginationTable.currentPage} totalPages={paginationTable.totalPages} onPageChange={onHandlePageChange} numberOfElements={paginationTable.numberOfElements} />
				)}
			</div>
		</div>
	);
};

export default PartnersTable;
